import React, { useEffect, useRef, useState } from 'react';
import AppShell from '@/components/AppShell';
import { useAuth } from '@/lib/auth';
import { extractError } from '@/lib/errors';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Building2, Pencil, Upload, Trash2, Split as SplitIcon, FilePlus2, Wrench, Coins, Hash, FileArchive, RotateCcw, Loader2, Palette } from 'lucide-react';
import { formatDate } from '@/lib/format';
import { Navigate } from 'react-router-dom';
import { CompanyRestoreAsNewDialog } from '@/components/admin/CompanyBackupRestoreDialogs';
import { CompanyRestoreDataDialog } from '@/components/admin/CompanyDataTransferDialog';
import { CompanySplitDialog, CompanyHardDeleteDialog } from '@/components/admin/CompanyLifecycleDialogs';
import { LedgerBackfillDialog } from '@/components/admin/LedgerBackfillDialog';
import { GoldBackfillDialog } from '@/components/admin/GoldBackfillDialog';

// Preset terms a company can pick for its running-number system. Companies
// may also choose "Custom…" and type their own vocabulary (e.g. "Lot No.").
const REFERENCE_LABEL_PRESETS = ['Reference No.', 'Page No.', 'Job No.', 'Order No.'];

export default function AdminCompanies() {
  const { isSuperAdmin } = useAuth();
  if (!isSuperAdmin) return <Navigate to="/dashboard" replace />;
  return (
    <AppShell title="Companies" subtitle="Super Admin: create and manage companies in the portal.">
      <CompaniesContent />
    </AppShell>
  );
}

function CompaniesContent() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', address: '', gstin: '', phone: '', email: '', logo_data_url: '', uses_reference_numbers: false, reference_label: 'Page No.', uses_design_numbers: false });
  // Whether the term selector is showing a preset or a free-text custom entry.
  const [refMode, setRefMode] = useState('preset');
  const fileRef = useRef(null);
  // Wave 3.27 — legacy encrypted-XLSX Backup/Restore removed from the UI.
  // Company Export/Restore (ZIP: JSON + images) — Super Admin self-service.
  const [restoreDataTarget, setRestoreDataTarget] = useState(null);
  const [exportingId, setExportingId] = useState(null);
  // Wave 3.28 — Split / Hard Delete / Restore-as-new
  const [splitTarget, setSplitTarget] = useState(null);
  const [hardDeleteTarget, setHardDeleteTarget] = useState(null);
  const [restoreAsNewOpen, setRestoreAsNewOpen] = useState(false);
  // Wave 3.61 — Ledger tally repair (backfill)
  const [ledgerBackfillOpen, setLedgerBackfillOpen] = useState(false);
  // Wave 3.99e — Gold Requirement backfill from CAD (super admin manual trigger)
  const [goldBackfillOpen, setGoldBackfillOpen] = useState(false);

  const load = async () => {
    const { data } = await api.get('/companies/'); setList(data);
  };
  useEffect(() => { load(); }, []);

  // Company Export (ZIP) — authenticated blob download.
  const exportCompany = async (c) => {
    setExportingId(c.id);
    try {
      const res = await api.get(`/admin/companies/${c.id}/export`, { responseType: 'blob' });
      // Derive filename from the Content-Disposition header when present.
      let filename = `${(c.name || 'company').replace(/[^A-Za-z0-9._-]+/g, '_')}_export.zip`;
      const cd = res.headers?.['content-disposition'] || '';
      const m = /filename="?([^"]+)"?/.exec(cd);
      if (m && m[1]) filename = m[1];
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Export ready — ${filename}`);
    } catch (err) {
      toast.error(extractError(err, 'Export failed'));
    } finally {
      setExportingId(null);
    }
  };

  const onLogoChange = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 1024 * 1024) { toast.error('Logo must be under 1 MB'); return; }
    const r = new FileReader();
    r.onload = () => setForm((p) => ({ ...p, logo_data_url: r.result }));
    r.readAsDataURL(f);
  };

  const openDialog = (c) => {
    if (c) {
      const lbl = (c.reference_label || 'Page No.');
      setRefMode(REFERENCE_LABEL_PRESETS.includes(lbl) ? 'preset' : 'custom');
      setEditing(c); setForm({
        name: c.name, address: c.address || '', gstin: c.gstin || '',
        phone: c.phone || '', email: c.email || '', logo_data_url: c.logo_data_url || '',
        uses_reference_numbers: !!c.uses_reference_numbers,
        reference_label: lbl,
        uses_design_numbers: !!c.uses_design_numbers,
      });
    } else {
      setRefMode('preset');
      setEditing(null); setForm({ name: '', address: '', gstin: '', phone: '', email: '', logo_data_url: '', uses_reference_numbers: false, reference_label: 'Page No.', uses_design_numbers: false });
    }
    setOpen(true);
  };
  const submit = async () => {
    try {
      // Never send an empty term; fall back to the default so the API keeps a
      // sensible label.
      const payload = { ...form, reference_label: (form.reference_label || '').trim() || 'Page No.' };
      if (editing) await api.put(`/companies/${editing.id}`, payload);
      else await api.post('/companies/', payload);
      toast.success(editing ? 'Company updated' : 'Company created');
      setOpen(false); load();
    } catch (e) { toast.error(extractError(e, 'Failed')); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-base font-medium text-[var(--ink-navy)]">All Companies</div>
          <div className="text-xs text-slate-500">Each company has fully isolated data and its own users.</div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setGoldBackfillOpen(true)}
            data-testid="gold-backfill-trigger"
            title="Backfill Gold Requirement from CAD weight where CAM was already confirmed before the auto-generate hook existed."
          >
            <Coins size={14} className="mr-1.5" /> Gold Backfill
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setLedgerBackfillOpen(true)}
            data-testid="ledger-backfill-trigger"
            title="Run ledger tally repair (dry-run or apply). Recomputes running balances across every packet ledger."
          >
            <Wrench size={14} className="mr-1.5" /> Ledger Repair
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setRestoreAsNewOpen(true)}
            data-testid="restore-as-new-trigger"
          >
            <FilePlus2 size={14} className="mr-1.5" /> Restore to New
          </Button>
          <Button className="luxury-button" onClick={() => openDialog(null)} data-testid="company-add-button">
            <Plus size={14} className="mr-1.5" /> New Company
          </Button>
        </div>
      </div>
      <Card className="overflow-hidden">
        <div className="ledger-table">
          <table className="w-full">
            <thead><tr>
              <th className="text-left">Company</th>
              <th className="text-left">GSTIN</th>
              <th className="text-right">Users</th>
              <th className="text-left">Status</th>
              <th className="text-left">Created</th>
              <th className="text-right">Actions</th>
            </tr></thead>
            <tbody data-testid="companies-table-body">
              {list.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-slate-500">No companies yet. Create the first one.</td></tr>}
              {list.map((c) => (
                <tr key={c.id} data-testid={`company-row-${c.id}`}>
                  <td>
                    <div className="flex items-center gap-2">
                      {c.logo_data_url ? <img src={c.logo_data_url} alt="" className="w-7 h-7 rounded object-cover" /> : <div className="w-7 h-7 rounded bg-slate-100 flex items-center justify-center"><Building2 size={14} className="text-slate-400" /></div>}
                      <div>
                        <div className="font-medium text-slate-800">{c.name}</div>
                        <div className="text-xs text-slate-500">{c.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="font-mono text-xs">{c.gstin}</td>
                  <td className="text-right font-mono tabular-nums">{c.user_count || 0}</td>
                  <td>{c.disabled ? <Badge className="bg-rose-50 text-rose-700">Disabled</Badge> : <Badge className="bg-emerald-50 text-emerald-700">Active</Badge>}{c.uses_reference_numbers && <Badge className="bg-indigo-50 text-indigo-700 ml-1" data-testid={`company-ref-badge-${c.id}`}><Hash size={10} className="inline mr-0.5" />{c.reference_label || 'Page No.'}</Badge>}{c.uses_design_numbers && <Badge className="bg-indigo-50 text-indigo-700 ml-1" data-testid={`company-design-badge-${c.id}`}><Palette size={10} className="inline mr-0.5" />Design No.</Badge>}</td>
                  <td className="text-xs text-slate-500">{formatDate(c.created_at)}</td>
                  <td className="text-right whitespace-nowrap">
                    <button onClick={() => exportCompany(c)} disabled={exportingId === c.id} className="text-xs text-violet-700 hover:underline mr-2 disabled:opacity-50" data-testid={`company-export-${c.id}`}>{exportingId === c.id ? <Loader2 size={12} className="inline mr-1 animate-spin" /> : <FileArchive size={12} className="inline mr-1" />}Export</button>
                    <button onClick={() => setRestoreDataTarget(c)} className="text-xs text-violet-700 hover:underline mr-2" data-testid={`company-restore-data-${c.id}`}><RotateCcw size={12} className="inline mr-1" />Restore Data</button>
                    <button onClick={() => setSplitTarget(c)} className="text-xs text-indigo-700 hover:underline mr-2" data-testid={`company-split-${c.id}`}><SplitIcon size={12} className="inline mr-1" />Split</button>
                    <button onClick={() => openDialog(c)} className="text-xs text-[var(--ink-navy)] hover:underline mr-2" data-testid={`company-edit-${c.id}`}><Pencil size={12} className="inline mr-1" />Edit</button>
                    <button onClick={() => setHardDeleteTarget(c)} className="text-xs text-rose-600 hover:underline" data-testid={`company-delete-${c.id}`}><Trash2 size={12} className="inline mr-1" />Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit Company' : 'New Company'}</DialogTitle><DialogDescription>Logo will appear on PDF/Excel reports.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 flex items-center gap-4">
              <div className="w-20 h-20 rounded-lg border-2 border-dashed border-[var(--divider)] flex items-center justify-center bg-[var(--paper-warm)] overflow-hidden">
                {form.logo_data_url ? <img src={form.logo_data_url} alt="" className="w-full h-full object-cover" /> : <Building2 size={24} className="text-slate-400" />}
              </div>
              <div>
                <input ref={fileRef} type="file" accept="image/*" hidden onChange={onLogoChange} data-testid="company-logo-input" />
                <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}><Upload size={14} className="mr-1.5" /> Upload Logo</Button>
                {form.logo_data_url && <Button variant="ghost" size="sm" onClick={() => setForm({ ...form, logo_data_url: '' })} className="ml-2 text-rose-600">Remove</Button>}
                <div className="text-[11px] text-slate-500 mt-1">PNG / JPG / SVG / WEBP, up to 1 MB. Will appear on PDFs when branding is enabled.</div>
              </div>
            </div>
            <div className="col-span-2"><Label>Company Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="company-name-input" /></div>
            <div><Label>GSTIN</Label><Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div className="col-span-2"><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="col-span-2"><Label>Address</Label><Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div className="col-span-2 rounded-lg border border-[var(--divider)] bg-[var(--paper-warm)] px-3 py-3" data-testid="company-reference-toggle-row">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 text-sm font-medium text-slate-800">
                    <Hash size={14} className="text-indigo-600" /> Running Numbers
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    When ON, every new order in this company auto-gets a running number (starting 1001).
                    Operators can group related items (e.g. a necklace set) under one number.
                  </div>
                </div>
                <Switch
                  checked={!!form.uses_reference_numbers}
                  onCheckedChange={(v) => setForm({ ...form, uses_reference_numbers: !!v })}
                  data-testid="company-reference-toggle"
                />
              </div>
              {form.uses_reference_numbers && (
                <div className="mt-3 pt-3 border-t border-[var(--divider)]" data-testid="company-reference-label-row">
                  <Label className="text-[12px] text-slate-700">What should this number be called?</Label>
                  <div className="flex flex-col sm:flex-row gap-2 mt-1">
                    <Select
                      value={refMode === 'custom' ? '__custom__' : form.reference_label}
                      onValueChange={(v) => {
                        if (v === '__custom__') { setRefMode('custom'); setForm((f) => ({ ...f, reference_label: '' })); }
                        else { setRefMode('preset'); setForm((f) => ({ ...f, reference_label: v })); }
                      }}
                    >
                      <SelectTrigger className="sm:w-52" data-testid="company-reference-label-select">
                        <SelectValue placeholder="Choose a term" />
                      </SelectTrigger>
                      <SelectContent>
                        {REFERENCE_LABEL_PRESETS.map((p) => (
                          <SelectItem key={p} value={p}>{p}</SelectItem>
                        ))}
                        <SelectItem value="__custom__" data-testid="company-reference-label-option-custom">Custom…</SelectItem>
                      </SelectContent>
                    </Select>
                    {refMode === 'custom' && (
                      <Input
                        value={form.reference_label}
                        onChange={(e) => setForm({ ...form, reference_label: e.target.value })}
                        placeholder="e.g. Lot No., Docket No."
                        maxLength={40}
                        className="flex-1"
                        data-testid="company-reference-label-input"
                      />
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1.5">
                    This term appears across the app (filters, order cards, Order Detail) and on the printed Order Form PDF.
                    Preview: <span className="font-medium text-indigo-700">{(form.reference_label || 'Page No.').trim() || 'Page No.'} 1001</span>
                  </div>
                </div>
              )}
            </div>
            <div className="col-span-2 rounded-lg border border-[var(--divider)] bg-[var(--paper-warm)] px-3 py-3" data-testid="company-design-toggle-row">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 text-sm font-medium text-slate-800">
                    <Palette size={14} className="text-indigo-600" /> Design Numbers
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    When ON, the Design Master module is active and every new order MUST carry a Design Number
                    (RD-101, RD-102…). Operators pick an existing design or create a new one at order time.
                  </div>
                </div>
                <Switch
                  checked={!!form.uses_design_numbers}
                  onCheckedChange={(v) => setForm({ ...form, uses_design_numbers: !!v })}
                  data-testid="company-design-toggle"
                />
              </div>
              {form.uses_design_numbers && (
                <div className="mt-2.5 pt-2.5 border-t border-[var(--divider)] text-[11px] text-slate-500" data-testid="company-design-preview-row">
                  Designs are numbered per company. Preview: <span className="font-mono font-medium text-indigo-700">RD-101</span>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} className="luxury-button" disabled={!form.name} data-testid="company-submit-button">{editing ? 'Save' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {restoreDataTarget && (
        <CompanyRestoreDataDialog
          company={restoreDataTarget}
          open={!!restoreDataTarget}
          onOpenChange={(v) => { if (!v) setRestoreDataTarget(null); }}
          onDone={() => load()}
        />
      )}

      {splitTarget && (
        <CompanySplitDialog
          company={splitTarget}
          open={!!splitTarget}
          onOpenChange={(v) => { if (!v) setSplitTarget(null); }}
          onSplit={() => load()}
        />
      )}

      {hardDeleteTarget && (
        <CompanyHardDeleteDialog
          company={hardDeleteTarget}
          open={!!hardDeleteTarget}
          onOpenChange={(v) => { if (!v) setHardDeleteTarget(null); }}
          onDeleted={() => { setHardDeleteTarget(null); load(); }}
        />
      )}

      <CompanyRestoreAsNewDialog
        open={restoreAsNewOpen}
        onOpenChange={setRestoreAsNewOpen}
        onRestored={() => load()}
      />

      <LedgerBackfillDialog
        open={ledgerBackfillOpen}
        onOpenChange={setLedgerBackfillOpen}
        companies={list}
      />

      <GoldBackfillDialog
        open={goldBackfillOpen}
        onOpenChange={setGoldBackfillOpen}
        companies={list}
      />
    </div>
  );
}
